// NOAA Coral Reef Watch (CRW) v3.1 daily 5 km product display definitions.
// Values are the native NOAA units; the portal only aggregates geometry for
// interactive display and never converts the underlying product values.
export const CRW_DAILY_PRODUCTS = {
  baa: {
    label: 'Bleaching Alert Area (7-day max)',
    shortLabel: 'Bleaching Alert Area',
    unit: 'category',
    categorical: true,
    description: 'NOAA CRW’s maximum bleaching alert category during the most recent seven days.',
    stops: [
      [0, '#87cfd2'], [1, '#ffe600'], [2, '#f6a800'], [3, '#ef120d'],
      [4, '#9d0606'], [5, '#a95326'], [6, '#e000d6'], [7, '#73006e'],
    ],
    legend: [
      ['No Stress', '#87cfd2'], ['Watch', '#ffe600'], ['Warning', '#f6a800'],
      ['Alert L1', '#ef120d'], ['Alert L2', '#9d0606'], ['Alert L3', '#a95326'],
      ['Alert L4', '#e000d6'], ['Alert L5', '#73006e'],
    ],
  },
  dhw: {
    label: 'Degree Heating Week',
    shortLabel: 'DHW',
    unit: '°C-weeks',
    description: 'Accumulated coral-bleaching heat stress over the previous 12 weeks.',
    // NOAA CRW heat-stress progression: no stress → watch → warning → alert.
    stops: [
      [0, '#d7eef2'], [1, '#fff3a1'], [2, '#ffd23f'], [4, '#ff9d00'],
      [8, '#ef3b2c'], [12, '#b10026'], [16, '#7a0177'], [20, '#3b0f70'],
    ],
    legend: [
      ['0', '#d7eef2'], ['1–<2', '#fff3a1'], ['2–<4', '#ffd23f'],
      ['4–<8', '#ff9d00'], ['8–<12', '#ef3b2c'], ['12–<16', '#b10026'], ['≥16', '#7a0177'],
    ],
  },
  hotspot: {
    label: 'Coral Bleaching HotSpot',
    shortLabel: 'HotSpot',
    unit: '°C',
    description: 'Instantaneous warming above the Maximum Monthly Mean climatology.',
    stops: [
      [-5, '#225ea8'], [-2, '#67a9cf'], [-0.5, '#d0e6f2'], [0, '#f7f7f7'],
      [0.5, '#fff176'], [1, '#ffb000'], [2, '#f03b20'], [4, '#a50f15'],
    ],
    legend: [
      ['≤−2', '#225ea8'], ['−2–0', '#67a9cf'], ['0', '#f7f7f7'],
      ['0–<1', '#fff176'], ['1–<2', '#ffb000'], ['2–<4', '#f03b20'], ['≥4', '#a50f15'],
    ],
  },
  coraltemp: {
    label: 'CoralTemp SST',
    shortLabel: 'CoralTemp',
    unit: '°C',
    description: 'Daily nighttime sea-surface foundation temperature.',
    stops: [
      [20, '#352a87'], [23, '#1464a0'], [25, '#2a9fd6'], [27, '#49c7a5'],
      [29, '#f4e61e'], [31, '#f99b1d'], [33, '#e5382d'], [35, '#a50026'],
    ],
    legend: [
      ['≤20', '#352a87'], ['23', '#1464a0'], ['25', '#2a9fd6'], ['27', '#49c7a5'],
      ['29', '#f4e61e'], ['31', '#f99b1d'], ['33', '#e5382d'], ['≥35', '#a50026'],
    ],
  },
  sst_anomaly: {
    label: 'SST Anomaly',
    shortLabel: 'SST Anomaly',
    unit: '°C',
    description: 'Daily CoralTemp departure from the NOAA CRW monthly climatology.',
    stops: [
      [-5, '#08306b'], [-3, '#2171b5'], [-1, '#6baed6'], [0, '#f7f7f7'],
      [1, '#fdae61'], [3, '#e6550d'], [5, '#a50f15'],
    ],
    legend: [
      ['≤−5', '#08306b'], ['−3', '#2171b5'], ['−1', '#6baed6'], ['0', '#f7f7f7'],
      ['+1', '#fdae61'], ['+3', '#e6550d'], ['≥+5', '#a50f15'],
    ],
  },
  sst_trend_7d: {
    label: 'SST Trend (7-day)',
    shortLabel: 'SST Trend',
    unit: '°C/day',
    description: 'Rate and direction of CoralTemp change over the preceding seven days.',
    stops: [
      [-1, '#08306b'], [-0.5, '#2171b5'], [-0.2, '#6baed6'], [0, '#45a34b'],
      [0.2, '#fdae61'], [0.5, '#e6550d'], [1, '#a50f15'],
    ],
    legend: [
      ['≤−1.0', '#08306b'], ['−0.5', '#2171b5'], ['−0.2', '#6baed6'],
      ['−0.2–+0.2', '#45a34b'], ['+0.2', '#fdae61'], ['+0.5', '#e6550d'], ['≥+1.0', '#a50f15'],
    ],
  },
};

export const crwProductFromVariable = (variable) => ({
  '7.4.4.1': 'dhw',
  '7.4.4.2': 'hotspot',
  '7.4.4.3': 'coraltemp',
  '7.4.4.4': 'sst_anomaly',
  '7.4.4.5': 'sst_trend_7d',
  '7.4.4.6': 'baa',
}[variable] || null);

const hexToRgb = (hex) => hex.match(/[a-f\d]{2}/gi).map(value => parseInt(value, 16));

export const crwColor = (productKey, value, alpha = 222) => {
  const stops = CRW_DAILY_PRODUCTS[productKey]?.stops;
  const numericValue = Number(value);
  if (!stops || !Number.isFinite(numericValue)) return [100, 116, 139, alpha];
  if (CRW_DAILY_PRODUCTS[productKey]?.categorical) {
    const category = Math.max(0, Math.min(7, Math.round(numericValue)));
    return [...hexToRgb(stops[category][1]), alpha];
  }
  if (numericValue <= stops[0][0]) return [...hexToRgb(stops[0][1]), alpha];
  if (numericValue >= stops[stops.length - 1][0]) return [...hexToRgb(stops[stops.length - 1][1]), alpha];
  const upperIndex = stops.findIndex(([threshold]) => numericValue < threshold);
  const [lowValue, lowColor] = stops[upperIndex - 1];
  const [highValue, highColor] = stops[upperIndex];
  const mix = (numericValue - lowValue) / (highValue - lowValue);
  const low = hexToRgb(lowColor);
  const high = hexToRgb(highColor);
  return low.map((channel, index) => Math.round(channel + (high[index] - channel) * mix)).concat(alpha);
};
